/**
 * Business date-range presets, resolved in the ACCOUNT's timezone.
 *
 * Why timezone matters here: an operator in America/Chicago asking for "This
 * month" means their business month. Computing the boundary in the browser's
 * timezone shifts it by hours, so a trip at 11:30 PM on the 31st either falls
 * out of the month or a trip from the 1st leaks in. Revenue reconciliation then
 * disagrees with the operator's own books by a day.
 *
 * Range semantics are HALF-OPEN: `[from, to)`.
 *
 * That is deliberate. An inclusive upper bound on a TIMESTAMPTZ column requires
 * picking a last instant (23:59:59.999), which silently drops anything in the
 * final millisecond and has to be recomputed around DST. A half-open range is
 * exact: "this month" is `>= Aug 1 00:00` AND `< Sep 1 00:00`, which stays
 * correct whether or not the month contains a clock change. The query builders
 * therefore apply `.gte(from)` and `.lt(to)`.
 */

import { dateInTimezone } from '../utils'
import type { DateRangeFilter } from '../types/reporting'

export type DatePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'ytd'
  | 'all_time'

export interface DatePreset {
  readonly key: DatePresetKey
  readonly label: string
}

export const DATE_PRESETS: readonly DatePreset[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'all_time', label: 'All time' },
]

/** Calendar Y/M/D as read in a specific timezone (month is 1-based). */
interface CalendarDate {
  readonly year: number
  readonly month: number
  readonly day: number
}

/**
 * What calendar day is it, in `timezone`, at instant `at`?
 *
 * Uses Intl rather than date arithmetic so DST and non-hour offsets (e.g.
 * Asia/Kolkata) are handled by the platform.
 */
export function calendarDateIn(at: Date, timezone: string): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)

  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0')

  return { year: get('year'), month: get('month'), day: get('day') }
}

const pad = (n: number): string => String(n).padStart(2, '0')

const toDateStr = ({ year, month, day }: CalendarDate): string =>
  `${year}-${pad(month)}-${pad(day)}`

/**
 * Midnight at the start of a calendar day in `timezone`, as a UTC instant.
 * Reuses the repo's existing `dateInTimezone` wall-clock converter.
 */
function startOfDayUtc(date: CalendarDate, timezone: string): Date {
  return dateInTimezone(toDateStr(date), '00:00', timezone)
}

/**
 * Shift a calendar date by whole days using UTC arithmetic on the calendar
 * values only. Safe because we are manipulating a Y/M/D triple, not an instant —
 * the triple is converted to an instant afterwards via the timezone.
 */
function addDays(date: CalendarDate, days: number): CalendarDate {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day))
  d.setUTCDate(d.getUTCDate() + days)
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  }
}

function addMonths(date: CalendarDate, months: number): CalendarDate {
  const d = new Date(Date.UTC(date.year, date.month - 1 + months, 1))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: 1 }
}

/**
 * Resolve a preset into a half-open UTC range.
 *
 * @param key      which preset
 * @param timezone the account's IANA timezone (e.g. 'America/Chicago')
 * @param now      reference instant; injected so tests can pin the clock
 */
export function resolveDatePreset(
  key: DatePresetKey,
  timezone: string,
  now: Date
): DateRangeFilter {
  const today = calendarDateIn(now, timezone)

  const range = (fromCal: CalendarDate, toCalExclusive: CalendarDate): DateRangeFilter => ({
    from: startOfDayUtc(fromCal, timezone).toISOString(),
    to: startOfDayUtc(toCalExclusive, timezone).toISOString(),
  })

  const tomorrow = addDays(today, 1)

  switch (key) {
    case 'today':
      return range(today, tomorrow)

    case 'yesterday':
      return range(addDays(today, -1), today)

    // "Last 7 days" includes today, so it spans 7 calendar days ending tonight.
    case 'last_7_days':
      return range(addDays(today, -6), tomorrow)

    case 'last_30_days':
      return range(addDays(today, -29), tomorrow)

    case 'this_month': {
      const first = { year: today.year, month: today.month, day: 1 }
      return range(first, addMonths(first, 1))
    }

    case 'last_month': {
      const firstThis = { year: today.year, month: today.month, day: 1 }
      return range(addMonths(firstThis, -1), firstThis)
    }

    case 'this_quarter': {
      const quarterStartMonth = Math.floor((today.month - 1) / 3) * 3 + 1
      const first = { year: today.year, month: quarterStartMonth, day: 1 }
      return range(first, addMonths(first, 3))
    }

    case 'ytd':
      return range({ year: today.year, month: 1, day: 1 }, tomorrow)

    // No bounds at all — the caller should omit the filter entirely.
    case 'all_time':
      return {}
  }
}
